import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { Context, getBexApiContext } from 'bernie-context';
import { LocalizedComponentProps } from 'bernie-l10n';

import { PropertyReviewsSummary } from '@shared-ui/lodging-reviews';

import { DialogContentModel, DialogContentSplitViewModel } from 'models/common';

import { BarRatings } from 'components/infosite/property-details/bar-ratings';
import { FindAnswersInput } from 'components/infosite/find-answers/find-answers-input';
import { PopularMentions } from 'components/infosite/property-details/popular-mentions';
import { RatingWithSuperlative } from 'components/common/reviews/review-components';
import { ReviewsDisclaimerTrigger } from 'components/infosite/property-details/reviews-disclaimer-trigger';
import { ReviewsFilter } from 'components/infosite/property-details/reviews-filter';
import { ReviewsListOnDialog } from 'components/infosite/property-details/reviews-list-on-dialog';

interface PropertyReviewsSummaryProps {
  context: Context;
  propertyId: number;
}

export const DialogContentSplitView = ({
  applyTravelerTypeFilters,
  context,
  l10n,
  onMoreReviews,
  propertyId,
  questionAnswersViewModel,
  reviews,
  travelerTypeFilterOptions,
  applyCommonPhrasesFilters
}: DialogContentSplitViewModel & PropertyReviewsSummaryProps) => {
  const { count, localizedCount, localizedOverallRating, reviewDisclaimer, superlative, overallRating } = reviews;
  const onMoreRecentReviews = () => onMoreReviews(true);
  const countMessage = count <= 1 ? 'hotels.infosite.reviews.verifiedReview.text.single' : 'hotels.infosite.reviews.verifiedReview.text';
  const verifiedReviewCountMessage = l10n.formatText(countMessage, localizedCount);

  return (
    <div className="uitk-grid l-x-padding-twelve xl-x-padding-twelve l-t-padding-four xl-t-padding-four">
      <div className="uitk-cell all-cell-1-3 all-r-padding-three l-l-padding-six xl-l-padding-twelve">
        <AbacusExperiment name="PWA_Shared_UI_Reviews_Summary">
          <AbacusControl>
            <>
              {localizedOverallRating && <RatingWithSuperlative rating={localizedOverallRating} superlative={superlative} />}
              <ReviewsCount
                count={count}
                localizedCount={localizedCount}
                verifiedReviewCountMessage={verifiedReviewCountMessage}
                reviewDisclaimer={reviewDisclaimer}
              />
              <BarRatings
                className="uitk-grid all-cell-1-1 all-x-gutter-four all-y-gutter-four l-b-padding-four xl-b-padding-four"
                barClassName="uitk-cell"
                reviews={reviews}
                l10n={l10n}
              />
            </>
          </AbacusControl>
          <AbacusVariant bucket={1}>
            <PropertyReviewsSummary inputs={{ context: getBexApiContext(context), propertyId: String(propertyId) }} />
          </AbacusVariant>
        </AbacusExperiment>
        {questionAnswersViewModel && <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} reviewsDialogInput={true} />}
        <ReviewsFilter
          applyReviewsFilter={applyTravelerTypeFilters}
          checkboxOptions={travelerTypeFilterOptions}
          label={l10n.formatText('hotels.infosite.reviews.filterLabel.travelerType')}
        />
      </div>
      <div className="uitk-cell all-cell-fill all-l-padding-three l-r-padding-six xl-r-padding-twelve">
        {overallRating && overallRating > 3.4 && (
          <AbacusExperiment name="PWA_HIS_DATELESS_PDP_Reviews_common_phrases_filter">
            <AbacusControl />
            <AbacusVariant bucket={1}>
              <PopularMentions applyCommonPhrasesFilters={applyCommonPhrasesFilters} />
            </AbacusVariant>
          </AbacusExperiment>
        )}
        <ReviewsListOnDialog onMoreReviews={onMoreRecentReviews} areTabsPresent={true} hideTopPadding={true} />
      </div>
    </div>
  );
};
DialogContentSplitView['displayName'] = 'DialogContentSplitView';

export const DialogContent = ({
  reviews,
  questionAnswersViewModel,
  l10n,
  onMoreReviews,
  reviewsListSectionTag,
  applyTravelerTypeFilters,
  applyCommonPhrasesFilters,
  context,
  propertyId,
  travelerTypeFilterOptions,
  travelerFilterCount,
  showTravelerFilterSheet,
  toggleTravelerFilterSheet
}: DialogContentModel & PropertyReviewsSummaryProps) => {
  const { superlative, count, localizedCount, localizedOverallRating, reviewDisclaimer, overallRating } = reviews;
  const onMoreRecentReviews = () => onMoreReviews(true);
  const countMessage = count <= 1 ? 'hotels.infosite.reviews.verifiedReview.text.single' : 'hotels.infosite.reviews.verifiedReview.text';
  const verifiedReviewCountMessage = l10n.formatText(countMessage, localizedCount);

  const barRatingClasses = [
    'uitk-grid',
    's-cell-1-1',
    'all-cell-1-2',
    'all-x-gutter-four',
    'all-y-gutter-four',
    'all-b-padding-four',
    'uitk-card-separator-bottom'
  ];

  return (
    <>
      <section className="reviews-gutter-horz bar-ratings s-t-padding-two m-t-padding-four l-t-padding-four xl-t-padding-four">
        <AbacusExperiment name="PWA_Shared_UI_Reviews_Summary">
          <AbacusControl>
            <>
              {localizedOverallRating && <RatingWithSuperlative rating={localizedOverallRating} superlative={superlative} />}
              <ReviewsCount
                count={count}
                localizedCount={localizedCount}
                verifiedReviewCountMessage={verifiedReviewCountMessage}
                reviewDisclaimer={reviewDisclaimer}
              />
              <BarRatings className={barRatingClasses.join(' ')} barClassName="uitk-cell" reviews={reviews} l10n={l10n} />
            </>
          </AbacusControl>
          <AbacusVariant bucket={1}>
            <PropertyReviewsSummary inputs={{ context: getBexApiContext(context), propertyId: String(propertyId) }} />
          </AbacusVariant>
        </AbacusExperiment>
      </section>
      <section className={reviewsListSectionTag} data-stid="reviews-list-section">
        {questionAnswersViewModel && <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} reviewsDialogInput={true} />}
        <>
          <span className="uitk-flex uitk-flex-gap-two uitk-flex-wrap">
            <ReviewsFilter
              applyReviewsFilter={applyTravelerTypeFilters}
              checkboxOptions={travelerTypeFilterOptions}
              countSelected={travelerFilterCount}
              label={l10n.formatText('hotels.infosite.reviews.filterLabel.travelerType')}
              showSheet={showTravelerFilterSheet}
              toggleSheet={toggleTravelerFilterSheet}
            />
          </span>
          {overallRating && overallRating > 3.4 && (
            <AbacusExperiment name="PWA_HIS_DATELESS_PDP_Reviews_common_phrases_filter">
              <AbacusControl />
              <AbacusVariant bucket={1}>
                <PopularMentions applyCommonPhrasesFilters={applyCommonPhrasesFilters} />
              </AbacusVariant>
            </AbacusExperiment>
          )}
          <ReviewsListOnDialog onMoreReviews={onMoreRecentReviews} hideTopPadding={false} />
        </>
      </section>
    </>
  );
};
DialogContent['displayName'] = 'DialogContent';

export interface ReviewsCountProps extends LocalizedComponentProps {
  count?: number;
  localizedCount?: string;
  verifiedReviewCountMessage?: string;
  reviewDisclaimer?: string;
}

export const ReviewsCount: React.FunctionComponent<ReviewsCountProps> = ({
  count,
  localizedCount,
  verifiedReviewCountMessage,
  reviewDisclaimer
}) => count && localizedCount && <ReviewsDisclaimerTrigger countMessage={verifiedReviewCountMessage} reviewDisclaimer={reviewDisclaimer} />;
ReviewsCount['displayName'] = 'ReviewsCount';
